import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, orderBy, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { MessageSquare, Plus, Search, X, Send, Trash2, ChevronLeft, Image as ImageIcon } from 'lucide-react';
import PageHeader from './common/PageHeader';

function CommunityBoard({ user }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [formData, setFormData] = useState({ title: '', content: '' });
    const [formImages, setFormImages] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const q = query(collection(db, 'communityPosts'), where('status', '==', 'active'));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setPosts(data);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!selectedPost) return;
        const q = query(collection(db, 'communityPosts', selectedPost.id, 'comments'), orderBy('createdAt', 'asc'));
        const unsub = onSnapshot(q, (snap) => {
            setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [selectedPost]);

    const filtered = posts.filter(p => {
        if (filter === 'my' && p.authorId !== user?.uid) return false;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return (p.title || '').toLowerCase().includes(s) || (p.content || '').toLowerCase().includes(s);
        }
        return true;
    });

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (formImages.length + files.length > 5) { alert('이미지는 최대 5개까지 가능합니다.'); return; }
        setFormImages(prev => [...prev, ...files]);
    };

    const handleSubmit = async () => {
        if (!formData.title.trim() || !formData.content.trim()) { alert('제목과 내용을 입력해주세요.'); return; }
        setSubmitting(true);
        try {
            let imageUrls = [];
            for (const file of formImages) {
                const r = ref(storage, `communityPosts/${user.uid}/${Date.now()}_${file.name}`);
                await uploadBytes(r, file);
                imageUrls.push(await getDownloadURL(r));
            }
            await addDoc(collection(db, 'communityPosts'), {
                title: formData.title.trim(), content: formData.content.trim(), images: imageUrls,
                authorId: user.uid, authorName: user.displayName || user.name || user.email || '',
                authorCompany: user.businessName || '', authorBusinessType: user.businessType || '',
                views: 0, likeCount: 0, commentCount: 0,
                createdAt: serverTimestamp(), updatedAt: serverTimestamp(), status: 'active',
            });
            setFormData({ title: '', content: '' }); setFormImages([]); setShowForm(false);
        } catch (e) { alert('등록 실패: ' + e.message); }
        setSubmitting(false);
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('게시물을 삭제하시겠습니까?')) return;
        await updateDoc(doc(db, 'communityPosts', postId), { status: 'deleted', updatedAt: serverTimestamp() });
        setSelectedPost(null);
    };

    const handleAddComment = async () => {
        if (!commentText.trim() || !selectedPost) return;
        await addDoc(collection(db, 'communityPosts', selectedPost.id, 'comments'), {
            text: commentText.trim(), authorId: user.uid,
            authorName: user.displayName || user.name || user.email || '',
            authorCompany: user.businessName || '', createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'communityPosts', selectedPost.id), { commentCount: (selectedPost.commentCount || 0) + 1 });
        setCommentText('');
    };

    const handleDeleteComment = async (commentId) => {
        if (!selectedPost) return;
        const { deleteDoc: delDoc } = await import('firebase/firestore');
        await delDoc(doc(db, 'communityPosts', selectedPost.id, 'comments', commentId));
        await updateDoc(doc(db, 'communityPosts', selectedPost.id), { commentCount: Math.max((selectedPost.commentCount || 1) - 1, 0) });
    };

    const fmtDate = (ts) => { if (!ts) return ''; const d = ts.toDate ? ts.toDate() : new Date(ts); return d.toLocaleDateString('ko-KR'); };

    if (loading) return <div style={S.loading}>로딩 중...</div>;

    if (selectedPost) return (
        <div style={S.container}>
            <button onClick={() => setSelectedPost(null)} style={S.backBtn}><ChevronLeft size={18} /> 목록으로</button>
            {selectedPost.images?.length > 0 && (
                <div style={S.imageGrid}>
                    {selectedPost.images.map((url, i) => <img key={i} src={url} alt="" style={S.detailImg} />)}
                </div>
            )}
            <h2 style={S.detailTitle}>{selectedPost.title}</h2>
            <div style={S.authorRow}>
                <span style={S.authorName}>{selectedPost.authorName}</span>
                {selectedPost.authorCompany && <span style={S.authorCompany}>{selectedPost.authorCompany}</span>}
                <span style={S.dateText}>{fmtDate(selectedPost.createdAt)}</span>
                {selectedPost.authorId === user?.uid && (
                    <button onClick={() => handleDelete(selectedPost.id)} style={S.deleteBtn}><Trash2 size={14} /> 삭제</button>
                )}
            </div>
            <p style={S.detailContent}>{selectedPost.content}</p>
            <div style={S.divider} />
            <h3 style={S.commentTitle}>댓글 {comments.length}</h3>
            {comments.map(c => (
                <div key={c.id} style={S.commentCard}>
                    <div style={S.commentHeader}>
                        <strong>{c.authorName}</strong>
                        {c.authorCompany && <span style={S.commentCompany}>{c.authorCompany}</span>}
                        <span style={S.commentDate}>{fmtDate(c.createdAt)}</span>
                        {c.authorId === user?.uid && <button onClick={() => handleDeleteComment(c.id)} style={S.commentDel}><X size={12} /></button>}
                    </div>
                    <p style={S.commentText}>{c.text}</p>
                </div>
            ))}
            <div style={S.commentInput}>
                <input value={commentText} onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                    placeholder="댓글을 입력하세요" style={S.commentField} />
                <button onClick={handleAddComment} style={S.sendBtn}><Send size={16} /></button>
            </div>
        </div>
    );

    return (
        <div style={S.container}>
            <PageHeader
                icon={MessageSquare}
                title="자유게시판"
                actions={
                    <button onClick={() => setShowForm(true)} style={S.createBtn}><Plus size={18} /> 글쓰기</button>
                }
            >
                <div style={S.filterRow}>
                    {['all', 'my'].map(f => (
                        <button key={f} onClick={() => setFilter(f)}
                            style={{ ...S.filterBtn, ...(filter === f ? S.filterActive : {}) }}>
                            {f === 'all' ? '전체글' : '내 글'}
                        </button>
                    ))}
                </div>
            </PageHeader>
            <div style={S.toolbar}>
                <div style={S.searchBox}>
                    <Search size={16} color="#9ca3af" />
                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        placeholder="검색" style={S.searchInput} />
                </div>
            </div>

            {showForm && (
                <div style={S.modal} onClick={() => setShowForm(false)}>
                    <div style={S.modalContent} onClick={e => e.stopPropagation()}>
                        <h2 style={{ margin: '0 0 16px' }}>글쓰기</h2>
                        <input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                            placeholder="제목" style={S.input} />
                        <textarea value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })}
                            placeholder="내용을 입력하세요" rows={6} style={{ ...S.input, resize: 'vertical' }} />
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                            {formImages.map((f, i) => (
                                <div key={i} style={S.thumbWrap}>
                                    <img src={URL.createObjectURL(f)} alt="" style={S.thumb} />
                                    <button onClick={() => setFormImages(prev => prev.filter((_, j) => j !== i))} style={S.thumbDel}><X size={10} /></button>
                                </div>
                            ))}
                            {formImages.length < 5 && (
                                <label style={S.addImgBtn}><ImageIcon size={20} /><input type="file" accept="image/*" multiple onChange={handleImageChange} hidden /></label>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowForm(false)} style={S.cancelBtn}>취소</button>
                            <button onClick={handleSubmit} disabled={submitting} style={S.submitBtn}>{submitting ? '등록 중...' : '등록'}</button>
                        </div>
                    </div>
                </div>
            )}

            {filtered.length === 0 ? (
                <div style={S.empty}><MessageSquare size={48} color="#d1d5db" /><p>게시물이 없습니다</p></div>
            ) : (
                <div style={S.list}>
                    {filtered.map(p => (
                        <div key={p.id} style={S.card} onClick={() => setSelectedPost(p)}>
                            <div style={{ flex: 1 }}>
                                <h3 style={S.cardTitle}>{p.title}</h3>
                                <p style={S.cardContent}>{(p.content || '').slice(0, 100)}</p>
                                <div style={S.cardMeta}>
                                    {p.authorCompany && <span style={S.metaCompany}>{p.authorCompany}</span>}
                                    <span>{p.authorName}</span>
                                    {p.commentCount > 0 && <span style={S.metaComment}>💬 {p.commentCount}</span>}
                                    <span>{fmtDate(p.createdAt)}</span>
                                </div>
                            </div>
                            {p.images?.length > 0 && <img src={p.images[0]} alt="" style={S.cardThumb} />}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const S = {
    container: { maxWidth: 800, margin: '0 auto', padding: 24 },
    loading: { textAlign: 'center', padding: 48, color: '#6b7280' },
    header: { marginBottom: 20 },
    title: { display: 'flex', alignItems: 'center', gap: 12, fontSize: 24, fontWeight: 700, margin: 0 },
    toolbar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
    filterRow: { display: 'flex', gap: 6 },
    filterBtn: { padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    filterActive: { background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' },
    searchBox: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, flex: 1, minWidth: 150 },
    searchInput: { border: 'none', outline: 'none', flex: 1, fontSize: 13 },
    createBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 },
    list: { display: 'flex', flexDirection: 'column', gap: 12 },
    card: { display: 'flex', gap: 12, padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, cursor: 'pointer' },
    cardTitle: { margin: '0 0 4px', fontSize: 15, fontWeight: 600 },
    cardContent: { margin: '0 0 8px', fontSize: 13, color: '#6b7280', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    cardMeta: { display: 'flex', gap: 8, fontSize: 11, color: '#9ca3af' },
    metaCompany: { color: '#6b7280' },
    metaComment: { color: '#f59e0b' },
    cardThumb: { width: 70, height: 70, borderRadius: 8, objectFit: 'cover', flexShrink: 0 },
    empty: { textAlign: 'center', padding: 48, color: '#9ca3af' },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: '#fff', borderRadius: 16, padding: 24, width: '90%', maxWidth: 500 },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' },
    cancelBtn: { padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer' },
    submitBtn: { padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
    thumbWrap: { position: 'relative', width: 60, height: 60 },
    thumb: { width: 60, height: 60, borderRadius: 8, objectFit: 'cover' },
    thumbDel: { position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    addImgBtn: { width: 60, height: 60, border: '1px dashed #d1d5db', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#9ca3af' },
    backBtn: { display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', marginBottom: 16, fontSize: 14 },
    imageGrid: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
    detailImg: { maxWidth: '100%', maxHeight: 300, borderRadius: 12, objectFit: 'cover' },
    detailTitle: { margin: '0 0 12px', fontSize: 22, fontWeight: 700 },
    authorRow: { display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: '#6b7280', marginBottom: 16, flexWrap: 'wrap' },
    authorName: { fontWeight: 600, color: '#1f2937' },
    authorCompany: { color: '#9ca3af' },
    dateText: { color: '#9ca3af' },
    deleteBtn: { display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto', padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
    detailContent: { whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 15, margin: 0 },
    divider: { borderTop: '1px solid #e5e7eb', margin: '24px 0 16px' },
    commentTitle: { margin: '0 0 12px', fontSize: 16, fontWeight: 600 },
    commentCard: { background: '#f9fafb', borderRadius: 10, padding: 12, marginBottom: 8 },
    commentHeader: { display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 6 },
    commentCompany: { color: '#9ca3af', fontSize: 12 },
    commentDate: { color: '#9ca3af', fontSize: 11, marginLeft: 'auto' },
    commentDel: { border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 },
    commentText: { margin: 0, fontSize: 14, lineHeight: 1.5 },
    commentInput: { display: 'flex', gap: 8, marginTop: 16, padding: '12px 0' },
    commentField: { flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 20, fontSize: 14, outline: 'none' },
    sendBtn: { width: 40, height: 40, borderRadius: '50%', background: '#f59e0b', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
};

export default CommunityBoard;
